/**
 * A test type's thresholds and interval — build 6B.
 *
 * Every threshold on the sample and on Madinah is proposed: a reference
 * value, not one confirmed against the aerodrome's certification basis. This
 * is where someone holding `rule:verify` changes the levels or the interval
 * and marks them proposed or agreed, saying what they were confirmed
 * against. Records already judged keep the thresholds they were judged by.
 */
import { useState } from "react";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useSetTestThresholds } from "../../api/entryFormQueries";
import { useSession } from "../../app/SessionContext";
import { Button, Field, Input, Segmented } from "../../design/components";
import { refusalText, thresholdsRefusal, thresholdToWire, type TargetStatus } from "../../domain/entryForms";
import type { TestType } from "../../domain/testing";
import { t } from "../../i18n";
import { when } from "./KpiTargetsCard";
import s from "./EntryForms.module.css";

const numOrNull = (v: string): number | null => (v.trim() === "" ? null : Number(v));

export function ThresholdsForm({ type }: { type: TestType }) {
  const session = useSession();
  const can = hasPermission(session.principal, PERMISSION.ruleVerify);
  const save = useSetTestThresholds();
  const [open, setOpen] = useState(false);
  const [levels, setLevels] = useState(() =>
    type.thresholds.map((th) => ({
      attention: th.attention == null ? "" : String(th.attention),
      fail: th.fail == null ? "" : String(th.fail),
    })),
  );
  const [every, setEvery] = useState(type.intervalDays != null ? String(type.intervalDays) : "");
  const [status, setStatus] = useState<TargetStatus>("agreed");
  const [basis, setBasis] = useState("");
  const [why, setWhy] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const allAgreed =
    type.thresholds.every((th) => th.status === "agreed") &&
    (type.ratio ? type.ratio.status === "agreed" : true) &&
    (type.intervalDays == null || type.intervalStatus === "agreed");

  const go = async () => {
    const thresholds = type.thresholds.length
      ? type.thresholds.map((th, i) =>
          thresholdToWire(th, { attention: numOrNull(levels[i].attention), fail: numOrNull(levels[i].fail) }),
        )
      : undefined;
    const input = {
      thresholds,
      intervalDays: every.trim() === "" ? undefined : Number(every),
      status,
      basis,
    };
    const refused = thresholdsRefusal(input);
    if (refused) return setWhy(refused);
    setWhy(null);
    try {
      await save.mutateAsync({ key: type.key, input });
      setSaved(status === "agreed" ? t("Saved as agreed.") : t("Saved as proposed."));
      setOpen(false);
    } catch (e) {
      setWhy(refusalText(e));
    }
  };

  return (
    <div data-testid={`thresholds-${type.key}`}>
      <p className={s.muted}>
        {allAgreed ? t("Agreed.") : t("Proposed — not yet confirmed against the certification basis.")}
        {type.updatedBy
          ? ` ${t("Last changed by {who}, {when}.", { who: type.updatedBy, when: when(type.updatedAt) })}`
          : ""}
      </p>
      {saved && (
        <p className={s.ok} role="status">
          {saved}
        </p>
      )}
      {!can ? (
        <p className={s.muted}>{t("Only an accountable manager can confirm or change thresholds.")}</p>
      ) : !open ? (
        <Button size="sm" onClick={() => setOpen(true)}>
          {t("Change or confirm")}
        </Button>
      ) : (
        <div className={s.form}>
          {type.thresholds.map((th, i) => (
            <div key={th.label} className={s.wide}>
              <b>{th.label}</b>{" "}
              <span className={s.muted}>
                {th.higherIsBetter === false ? t("lower is better") : t("higher is better")}
                {th.basis === "pct_of_specified" ? ` · ${t("% of specified")}` : type.unit ? ` · ${type.unit}` : ""}
              </span>
              <div className={s.form}>
                <Field label="Attention level">
                  <Input
                    type="number"
                    step="any"
                    value={levels[i].attention}
                    onChange={(e) =>
                      setLevels((l) => l.map((x, j) => (j === i ? { ...x, attention: e.target.value } : x)))
                    }
                    aria-label={t("Attention level for {label}", { label: th.label })}
                  />
                </Field>
                <Field label="Fail level">
                  <Input
                    type="number"
                    step="any"
                    value={levels[i].fail}
                    onChange={(e) => setLevels((l) => l.map((x, j) => (j === i ? { ...x, fail: e.target.value } : x)))}
                    aria-label={t("Fail level for {label}", { label: th.label })}
                  />
                </Field>
              </div>
            </div>
          ))}
          <Field label="Every (days)">
            <Input type="number" min={1} max={3650} step={1} value={every} onChange={(e) => setEvery(e.target.value)} />
          </Field>
          <div>
            <Segmented
              ariaLabel="Threshold status"
              value={status}
              onChange={setStatus}
              options={[
                { value: "proposed", label: "Proposed" },
                { value: "agreed", label: "Agreed" },
              ]}
            />
          </div>
          <div className={s.wide}>
            <Field label="Confirmed against">
              <Input
                value={basis}
                onChange={(e) => setBasis(e.target.value)}
                placeholder={t("e.g. GACAR Part 139, edition in force")}
              />
            </Field>
          </div>
          {why && (
            <p className={`${s.err} ${s.wide}`} role="alert">
              {why}
            </p>
          )}
          <div className={`${s.actions} ${s.wide}`}>
            <Button variant="primary" onClick={() => void go()} disabled={save.isPending}>
              {save.isPending ? t("Saving…") : t("Save thresholds")}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t("Cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
